import { apiEmployeeSettings } from "@/api/employees/employee-settings.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEmployeeHomeLayoutSetting,
  ApiEmployeeHomeLayoutSettingPayload,
  ApiEmployeeJobsPinsSettingPayload,
} from "@/types/employee-settings.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiNotificationsSettingPayload } from "@/types/notification.types.ts";

const employeeSettings = createQueryKeys("employeeSettings", {
  notifications: null,
  jobsPins: null,
  homeLayout: null,
});

export const useNotificationsSetting = () => {
  return useQuery({
    queryKey: employeeSettings.notifications.queryKey,
    queryFn: async () => {
      const result = await apiEmployeeSettings.getNotificationsSetting();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useNotificationsSettingSave = () => {
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (payload: ApiNotificationsSettingPayload) => {
      const result = await apiEmployeeSettings.saveNotificationsSetting(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeJobsPinsSetting = () => {
  return useQuery({
    queryKey: employeeSettings.jobsPins.queryKey,
    queryFn: async () => {
      const result = await apiEmployeeSettings.getJobsPinsSetting();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useEmployeeJobsPinsSettingSave = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiEmployeeJobsPinsSettingPayload) => {
      const result = await apiEmployeeSettings.saveJobsPinsSetting(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: employeeSettings.jobsPins.queryKey });
      const previous = queryClient.getQueryData(employeeSettings.jobsPins.queryKey);
      queryClient.setQueryData(employeeSettings.jobsPins.queryKey, payload);
      return { previous };
    },
    onError: (error: Error, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(employeeSettings.jobsPins.queryKey, context.previous);
      }
      onError(error);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(employeeSettings.jobsPins.queryKey, data);
    },
  });
};

export const useHomeLayout = () => {
  return useQuery({
    queryKey: employeeSettings.homeLayout.queryKey,
    queryFn: async () => {
      const result = await apiEmployeeSettings.getHomeLayout();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useHomeLayoutSave = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiEmployeeHomeLayoutSettingPayload) => {
      const result = await apiEmployeeSettings.saveHomeLayout(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: employeeSettings.homeLayout.queryKey });
      const previous = queryClient.getQueryData<ApiEmployeeHomeLayoutSetting>(
        employeeSettings.homeLayout.queryKey
      );
      if (previous) {
        const widgetById = new Map(previous.widgets.map((widget) => [widget.id, widget]));
        const widgets = payload.widgets
          .map(({ id, hidden }) => {
            const widget = widgetById.get(id);
            return widget ? { ...widget, hidden } : undefined;
          })
          .filter((widget) => widget !== undefined);
        queryClient.setQueryData<ApiEmployeeHomeLayoutSetting>(
          employeeSettings.homeLayout.queryKey,
          { ...previous, widgets, is_customized: true }
        );
      }
      return { previous };
    },
    onError: (error: Error, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(employeeSettings.homeLayout.queryKey, context.previous);
      }
      onError(error);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(employeeSettings.homeLayout.queryKey, data);
    },
  });
};

export const useHomeLayoutReset = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async () => {
      const result = await apiEmployeeSettings.resetHomeLayout();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(employeeSettings.homeLayout.queryKey, data);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
